import { API } from 'aws-amplify';
import { getAccessToken, getRefreshToken, setTokens, clearTokens, decodeJwt } from './authTokens';

const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';
const api_name = isCorporateMode 
    ? "Corporate-API"
    : (process.env.REACT_APP_STAGE === 'DEV' ? "Node-API" : "prod-api");

const PUBLIC_COMMANDS = new Set([
  'sendRegistrationRequest',
  'sendAssessmentCompletionEmail',
  'AdminCreateUser',
  'checkCognitoEmailExists',
  'AdminSetUserPassword',
  'login',
  'refreshToken',
]);

const isPublicCommand = (cmd) => PUBLIC_COMMANDS.has(String(cmd || ''));

let originalPost = null;
let refreshInFlight = null; // Promise

const parseResponse = (res) => {
  // Support amplify's { status, data } with nested .data.body possibly stringified
  const status = res?.status ?? res?.data?.statusCode ?? 200;
  let body = res?.data?.body ?? res?.data;
  try {
    if (typeof body === 'string') body = JSON.parse(body);
    if (body && typeof body.body === 'string') body = JSON.parse(body.body);
  } catch {}
  return { status, body, raw: res };
};

const getStatusFromError = (err) => {
  return err?.response?.status || err?.status || err?.data?.statusCode || err?.raw?.status || 0;
};

const doRefresh = async () => {
  const rt = getRefreshToken();
  if (!rt) throw new Error('NoRefreshToken');
  const params = {
    body: { command: 'refreshToken', refreshToken: rt },
    response: true,
    convertEmptyValues: true,
    headers: {}
  };
  const res = await originalPost(api_name, '', params);
  const { body } = parseResponse(res);
  if (!body || !body.accessToken) throw new Error('RefreshFailed');
  setTokens({ accessToken: body.accessToken, refreshToken: body.refreshToken }, true);
  return body;
};

const attachAuth = (params) => {
  const cmd = params?.body?.command;
  const headers = { ...(params?.headers || {}) };
  if (!isPublicCommand(cmd)) {
    const at = getAccessToken();
    if (at) {
      headers['Authorization'] = `Bearer ${at}`;
    }
  }
  return { ...params, headers };
};

export const installApiInterceptor = () => {
  if (originalPost) return; // already installed
  originalPost = API.post.bind(API);

  API.post = async (apiName, path, params = {}) => {
    // Do not attach auth for explicit refresh/login handled by isPublicCommand
    const initialParams = attachAuth(params);

    try {
      const res = await originalPost(apiName, path, initialParams);
      // Optional: preemptive refresh if exp near expiry
      try {
        const cmd = params?.body?.command;
        if (!isPublicCommand(cmd)) {
          const at = getAccessToken();
          const payload = at ? decodeJwt(at) : null;
          if (payload && payload.exp) {
            const now = Math.floor(Date.now() / 1000);
            const secondsLeft = payload.exp - now;
            if (secondsLeft > 0 && secondsLeft < 30 && !refreshInFlight) {
              refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
              await refreshInFlight;
            }
          }
        }
      } catch {}
      return res;
    } catch (err) {
      const status = getStatusFromError(err);
      const cmd = params?.body?.command;

      if (status === 401 && !isPublicCommand(cmd)) {
        try {
          if (!refreshInFlight) {
            refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
          }
          await refreshInFlight;
          const retried = await originalPost(apiName, path, attachAuth(params));
          return retried;
        } catch (refreshErr) {
          clearTokens();
          throw refreshErr;
        }
      }
      throw err;
    }
  };
};

export const login = async (email, pwd, isCorporate = false) => {
  const res = await originalPost(api_name, '', {
    body: { 
      command: 'login', 
      email,
      isCorporate: isCorporate,
      userType: isCorporate ? 'corporate' : 'regular'
    },
    response: true,
    convertEmptyValues: true,
    headers: {}
  });
  const { status, body } = parseResponse(res);
  
  // Handle error responses
  if (status === 403) {
    const error = new Error(body?.error || 'Account access disabled');
    error.status = 403;
    error.response = { status: 403, data: body };
    throw error;
  }
  
  if (!body || !body.accessToken) {
    const error = new Error(body?.error || body?.message || 'Login failed');
    error.status = status;
    error.response = { status, data: body };
    throw error;
  }
  
  setTokens({ accessToken: body.accessToken, refreshToken: body.refreshToken }, true);
  return body;
};

export const logout = () => {
  clearTokens();
};
